import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MEDIA } from "@/lib/content";
import { Button } from "@/components/ui/button";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 overflow-hidden"
    >
      {/* Vastu growth line — golden diagonal in top-right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 right-0 w-[280px] h-[280px] md:w-[400px] md:h-[400px] overflow-hidden"
      >
        <div
          className="absolute -top-2 -right-2 w-[420px] h-1.5 md:w-[560px] md:h-2 rounded-full"
          style={{
            background: "linear-gradient(90deg, transparent 0%, #D4A24C 60%, #B7894A 100%)",
            transform: "rotate(45deg)",
            transformOrigin: "right center",
            opacity: 0.85,
          }}
        />
        <div
          className="absolute top-3 -right-2 w-[320px] h-[1px] md:w-[440px]"
          style={{
            background: "linear-gradient(90deg, transparent 0%, #D4A24C 70%)",
            transform: "rotate(45deg)",
            transformOrigin: "right center",
            opacity: 0.5,
          }}
        />
      </div>

      <div className="container-outer pb-16 md:pb-24">
        {/* Overline */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="overline mb-8"
          data-testid="hero-overline"
        >
          EV Charging · Solar · Hybrid Inverters · PPA · IT Products
        </motion.p>

        {/* Asymmetric layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="md:col-span-7"
          >
            <h1
              className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.95] tracking-tightest font-medium text-primary"
              data-testid="hero-heading"
            >
              Clean energy
              <br />
              engineered for
              <br />
              <span className="italic font-light text-accent">real business.</span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="md:col-span-5 md:pl-8 md:border-l md:border-border"
          >
            <p
              className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-md"
              data-testid="hero-subheading"
            >
              We design, build and operate turnkey solar installations, EV
              charging networks and long-term Power Purchase Agreements — so
              enterprises can switch to renewables without the capex.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Button
                asChild
                size="lg"
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-7 transition-transform duration-200 hover:-translate-y-0.5"
                data-testid="hero-primary-cta"
              >
                <a href="#contact">Start a project</a>
              </Button>
              <Button
                asChild
                variant="ghost"
                size="lg"
                className="rounded-full h-12 px-6 hover:bg-secondary text-primary"
                data-testid="hero-secondary-cta"
              >
                <a href="#services" className="flex items-center gap-2">
                  Explore services <ArrowUpRight size={18} strokeWidth={1.75} />
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Full-bleed image strip */}
      <motion.div
        initial={{ opacity: 0, scale: 1.02 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.2 }}
        className="relative h-[52vh] md:h-[70vh] w-full overflow-hidden"
        data-testid="hero-image-wrapper"
      >
        <img
          src={MEDIA.hero}
          alt="Solar panels"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
      </motion.div>
    </section>
  );
}
